import {
  AfterViewInit,
  Component,
  OnDestroy,
  ElementRef, EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  ViewChild
} from '@angular/core';
import {Observable, Subject, throwError} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {FilterBase} from "../../../../../models/filter-base";
import {catchError, map} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../../../shared/models/apiurl.models";
import {LoadingMaskServiceService} from "../../../../../components/loading-frame/loading-frame.component";
import {FilterbaseService} from "../../filter-base/services/filterbase.service";
import {ApiResponseUtils} from "../../../../../utils/api_response_utils";


@Pipe({
    name: 'filterBasesFilter',
    standalone: false
})
export class FilterBasesFilter implements PipeTransform {
  transform(data: any[], filterType: any, value: any): any {
    if (!data || !filterType) return data;
    return data.filter(item => item[filterType] == value);
  }
}

export interface SelectOption {
  id: string;
  label: string;
  name: string;
  value: {
    name: string;
    value: string;
  }
}

@Component({
    selector: 'app-select-field',
    templateUrl: './select-field.component.html',
    styleUrls: ['./select-field.component.css'],
    standalone: false
})
export class SelectFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Ends the service subscriptions: they used to outlive the component, so a
   *  destroyed field (another category's) could still clear a filter with the
   *  same key. */
  private readonly destroy$ = new Subject<void>();

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  items: SelectOption[] = [];
  isOpened: boolean = false;
  selectedValue!: string;
  searchInput!: string;
  selectedObject!: any;
  isStaticField!: boolean;
  loadingData!: boolean;

  // ── multi-select ──
  isMultiSelect: boolean = false;
  selectedItems: any[] = [];

  @Input() filterBase!: FilterBase;
  @Input() widthSize: number = 160;
  @Input() hideTitle: boolean = false;
  @Input() placeholder!: string;
  @Input() range!: boolean;
  @Output() outValue = new EventEmitter<string>();

  private subject = new Subject<any>();

  @ViewChild('dropdown') dropdown!: ElementRef;
  @ViewChild('dropdownSearchField') dropdownSearchField!: ElementRef;
  @ViewChild('searchInputElement') searchInputElement!: ElementRef;
  @ViewChild('clearButton') clearButton!: ElementRef;
  @Input() direction!: string;

  constructor(
      private http: HttpClient,
      private apiKey: ApiUrl,
      private loadingMaskServiceService: LoadingMaskServiceService,
      private filterBaseService: FilterbaseService
  ) {
    this.filterBaseService.getRemoveEvent().pipe(takeUntil(this.destroy$)).subscribe((rs) => {
      if (rs == this.filterBase?.key) {
        this.clearField();
      }
    });

    this.filterBaseService.getClickEvent().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (!this.filterBase?.callBackFunction) return;
      const pathFetch = this.apiKey.pathTranslate(this.filterBase.callBackFunction, this.filterBaseService);
      if (pathFetch.requiredField.length > 0) {
        const relatedFields = pathFetch.requiredField.map(res => res.index);
        if (relatedFields.includes(res)) {
          this.items = [];
          this.selectedObject = null;
          this.selectedItems = [];
          this.selectedValue = '';
          this.filterBaseService.deleteQuestion(this.filterBase.key);
        }
      }
    });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Server placeholder (FilterBase.placeHolder), unless it is the 'NA'
   *  default; the range halves keep their own "מ־" / "עד". */
  get effectivePlaceholder(): string {
    if (this.placeholder) return this.placeholder;
    const server = (this.filterBase?.placeHolder || '').trim();
    return server && !['NA', 'None', 'none', 'null'].includes(server) ? server : 'בחרו אפשרות';
  }

  @HostListener('window:click', ['$event'])
  onClick(btn: any) {
    if (btn.target.id === 'searchInput') return;
    if (this.isMultiSelect && this.dropdown?.nativeElement?.contains(btn.target)) return;
    this.isOpened = false;
  }

  @HostListener('keydown.enter', ['$event'])
  enterBtn(event: KeyboardEvent) {
    this.isOpened = false;
  }

  @HostListener('keydown', ['$event'])
  selectBtBtn(event: KeyboardEvent) {
    if (this.isMultiSelect) return;

    if (event.key === 'ArrowDown') {
      const index = this.items.indexOf(this.selectedObject) === -1
          ? 0
          : this.items.indexOf(this.selectedObject);
      if (index < this.items.length - 1) {
        this.selectItem(this.items[index + 1]);
      }
      const dropdownList = this.dropdown.nativeElement.getElementsByClassName('dropdown-list')[0];
      const element = this.dropdown.nativeElement.getElementsByClassName('selectedRow')[0];
      if (element != undefined) element.scrollIntoView(false);
      dropdownList.scrollTop = dropdownList.scrollTop + 40;
    }

    if (event.key === 'ArrowUp') {
      const index = this.items.indexOf(this.selectedObject);
      if (index > 0) {
        this.selectItem(this.items[index - 1]);
      }
      const dropdownList = this.dropdown.nativeElement.getElementsByClassName('dropdown-list')[0];
      const element = this.dropdown.nativeElement.getElementsByClassName('selectedRow')[0];
      if (element != undefined) element.scrollIntoView(false);
    }
  }

  onClickFld() {
    setTimeout(() => this.searchInputElement?.nativeElement?.focus(), 0);
    const pathFetch = this.apiKey.pathTranslate(
        this.filterBase.callBackFunction,
        this.filterBaseService,
        this.filterBase.additionalParam
    );
    if (Array.isArray(this.items) && this.items.length === 0) {
      this.loadingData = true;
      this.fetchFilterData(pathFetch.path).subscribe(res => {
        this.loadingData = false;
        this.items = ApiResponseUtils.extractList(res);
      });
    }
  }

  initStaticSelect(): void {
    this.isStaticField = true;
    this.items = this.filterBase.options.map(item => ({
      id: item.id,
      label: item.value,
      name: this.filterBase.key,
      value: {
        name: item.value,
        value: item.key
      }
    }));
  }

  private handleObserverError(error: any): Observable<any> {
    return throwError(error.error || error);
  }

  fetchFilterData(url: string): Observable<any> {
    return this.http.get(url, this.options).pipe(
        map(res => res ? res : null),
        catchError(this.handleObserverError)
    );
  }

  initRemoteSelect(): void {
    if (this.filterBase.callBackFunction === null || this.filterBase.callBackFunction === 'None') return;
    // additionalParam (table_name=...) belongs to the request: without it
    // api/table-data/ answered rows of EVERY table.
    const pathFetch = this.apiKey.pathTranslate(this.filterBase.callBackFunction, this.filterBaseService,
        this.filterBase.additionalParam);
    if (pathFetch.requiredField.length > 0) {
      // dependent field — wait for parent to resolve
    } else {
      this.loadingData = true;
      this.fetchFilterData(pathFetch.path).subscribe(res => {
        this.items = ApiResponseUtils.extractList(res);
        this.loadingData = false;
      });
    }
  }

  ngOnInit(): void {
    // MultiSelectBox rows come as type multiSelect OR searchMultiSelect.
    this.isMultiSelect = this.filterBase.type === 'multiSelect' || this.filterBase.type === 'searchMultiSelect'
        || this.filterBase.controlType === 'MultiSelectBox';

    if (this.filterBase.options.length > 0) {
      this.initStaticSelect();
    } else {
      this.initRemoteSelect();
    }

    this.initPresetValue();
    this.restoreStickyValue();
  }

  restoreStickyValue(): void {
    // skip if already set by backend preset
    if (this.selectedObject || this.selectedItems.length > 0) return;

    const stored = this.filterBaseService.getQuestion(this.filterBase.key);
    if (!stored) return;

    if (this.isMultiSelect && Array.isArray(stored)) {
      this.selectedItems = stored;
      this.selectedValue = stored.length > 0 ? stored[0].label : '';
    } else {
      this.selectedValue = stored.label;
      this.selectedObject = stored;
    }
  }

  initPresetValue(): void {
    if (!this.filterBase.value || typeof this.filterBase.value !== 'object') return;

    if (this.isMultiSelect && Array.isArray(this.filterBase.value)) {
      this.selectedItems = this.filterBase.value as any[];
      this.selectedValue = this.selectedItems.length > 0 ? this.selectedItems[0].label : '';
      this.filterBaseService.addQuestion(this.filterBase.key, this.selectedItems);
    } else {
      const preset = this.filterBase.value as SelectOption;
      this.selectedValue = preset.label;
      this.selectedObject = preset;
      this.filterBaseService.addQuestion(this.filterBase.key, this.selectedObject);
    }
  }

  openMenu1(): void {
    setTimeout(() => {
      if (this.isMultiSelect) {
        this.isOpened = true;
      } else {
        this.isOpened = !this.isOpened;
      }
      setTimeout(() => {
        const dropdownList = this.dropdown.nativeElement.getElementsByClassName('dropdown-list')[0];
        const element = this.dropdown.nativeElement.getElementsByClassName('selectedRow')[0];
        if (element != undefined) {
          element.scrollIntoView(false);
          dropdownList.scrollTop = dropdownList.scrollTop + 120;
        }
      }, 2);
    }, 1);
  }

  openMenu(): void {
    if (this.filterBase.disabled) return;
    this.openMenu1();
    this.fieldSetting();
  }

  fieldSetting(): void {}

  selectItem(item: any, source: any = null) {
    if (this.isMultiSelect) {
      const idx = this.selectedItems.findIndex(i => i.id === item.id);
      if (idx === -1) {
        this.selectedItems = [...this.selectedItems, item];
      } else {
        this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
      }
      this.selectedValue = this.selectedItems.length > 0
          ? this.selectedItems[0].label
          : '';
      this.filterBaseService.addQuestion(this.filterBase.key, this.selectedItems);
      return;
    }

    this.selectedValue = item.label;
    this.selectedObject = item;
    if (this.range != undefined) {
      this.onChangeValue(this.selectedObject);
    } else {
      this.filterBaseService.addQuestion(this.filterBase.key, this.selectedObject);
    }
    if (source != null) {
      this.isOpened = false;
    }
  }

  isItemSelected(item: any): boolean {
    return this.selectedItems.some(i => i.id === item.id);
  }

  getClass(item: any): string {
    if (this.isMultiSelect) {
      return this.isItemSelected(item) ? 'selectedRow' : '';
    }
    return this.selectedObject === item ? 'selectedRow' : '';
  }

  get tooltipText(): string {
    if (!this.isMultiSelect || this.selectedItems.length === 0) return '';
    return this.selectedItems.map(i => i.label).join('\n');
  }

  get extraCount(): number {
    return this.selectedItems.length - 1;
  }

  get hasExtra(): boolean {
    return this.isMultiSelect && this.selectedItems.length > 1;
  }

  onBlur() {
    if (this.isMultiSelect) return;
    if (this.selectedValue == null || this.selectedValue === '') {
      this.selectedObject = null;
    }
    if (this.selectedObject != null) {
      this.selectedValue = this.selectedObject.label;
    } else {
      this.selectedValue = '';
    }
  }

  getDropdownListStyle() {
    return { width: '100%' };
  }

  getDynamicStyle() {
    return { width: '100%' };
  }

  onChange($event: any) {
    if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes($event.key)) return;
    if (this.isStaticField) return;
    const pathFetch = this.apiKey.pathTranslate(this.filterBase.callBackFunction, this.filterBaseService,
        this.filterBase.additionalParam);
    let url = pathFetch.path;
    if ($event != null && this.searchInput != null) {
      // The list URL may already carry a query (?region=..., table_name=...):
      // a second '?' broke it.
      url = url + (url.includes('?') ? '&' : '?') + 'q=' + encodeURIComponent(this.searchInput);
    }
    this.fetchFilterData(url).subscribe(res => {
      this.loadingData = false;
      this.items = ApiResponseUtils.extractList(res);
    });
  }

  searchInputClick() {}

  onChangeValue(delta: any) {
    this.outValue.emit(delta);
  }

  clearEventHandler() {
    this.filterBaseService.sendRemoveEvent(this.filterBase.key);
  }

  clearField() {
    this.filterBaseService.deleteQuestion(this.filterBase.key);
    this.selectedObject = null;
    this.selectedItems = [];
    this.selectedValue = '';
  }

  applyMultiSelect() {
    this.isOpened = false;
  }
}
